import type { ReactNode } from 'react'
import { useEffect, useRef } from 'react'
import { cn } from '@/shared/lib'
import { Icon } from '@/shared/ui/icon'

interface DialogProps {
  open: boolean
  onClose: () => void
  title: string
  children?: ReactNode
  footer?: ReactNode
}

// Нативный <dialog>: focus-trap и закрытие по Esc — из коробки, без ручной ловушки.
export function Dialog({ open, onClose, title, children, footer }: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    // Опциональный вызов: среды без нативного <dialog> (тест-DOM) не роняют компонент.
    if (open && !el.open) el.showModal?.()
    else if (!open && el.open) el.close?.()
  }, [open])

  // Клик по backdrop закрывает. target — сам <dialog>, но клик по его паддингу
  // тоже: отличаем по попаданию координат в бокс окна. Клавиатурный эквивалент
  // (Esc) даёт нативный <dialog>, поэтому слушатель — не на JSX (a11y-правила),
  // а на элементе.
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const onClick = (e: MouseEvent) => {
      if (e.target !== el) return
      const r = el.getBoundingClientRect()
      const inside =
        e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom
      if (!inside) onClose()
    }
    el.addEventListener('click', onClick)
    return () => el.removeEventListener('click', onClick)
  }, [onClose])

  return (
    <dialog
      ref={ref}
      onClose={onClose}
      className={cn(
        // Мобила: bottom-sheet во всю ширину (снизу, скруглён только верх),
        // с sm — центрированное модальное окно. max-h + overflow — длинный
        // контент (пикер фонов) скроллится внутри, не раздувая вьюпорт.
        'mx-0 mt-auto mb-0 max-h-[85dvh] w-full max-w-none overflow-y-auto rounded-t-card rounded-b-none border border-border-default bg-bg-elevated p-4 text-text-primary',
        'sm:m-auto sm:max-h-[90dvh] sm:w-[min(92vw,32rem)] sm:rounded-card sm:p-6',
        'backdrop:bg-black/70',
      )}
    >
      <div className="mb-4 flex items-start justify-between gap-4">
        <h2 className="t-h3">{title}</h2>
        <button
          type="button"
          aria-label="Закрыть"
          onClick={onClose}
          className="text-text-tertiary transition-colors hover:text-text-primary"
        >
          <Icon name="close" />
        </button>
      </div>
      {children && <div className="text-text-secondary">{children}</div>}
      {footer && <div className="mt-6 flex justify-end gap-3">{footer}</div>}
    </dialog>
  )
}
